import { Controller } from "@hotwired/stimulus"
import jquery from "jquery"

const SCOPE_KEY_BY_FIELD_ID = {
  ai_query_project_ids: "projects",
  ai_query_estimate_ids: "estimates",
  ai_query_user_ids: "users"
}

export default class extends Controller {
  static targets = ["field", "toggle", "startDate", "endDate"]
  static values = { optionsUrl: String }

  connect() {
    this.searchTerms = {}
    this.optionsRequestToken = 0
    this.isBulkUpdating = false
    this.isInitializingFields = false
    this.pendingRestoreTimer = null
    this.maxInitializeAttempts = 100
    this.initializeRetryTimer = null
    this.reinitializeDelayTimer = null
    this.optionsRequestDebounceTimer = null
    this.pendingOptionsRequestKey = null
    this.initializeAttempts = 0
    this.captureOriginalFieldNames()
    this.restoreFieldState()
    this.lastAppliedOptionsRequestKey = this.currentOptionsRequestKey()
    this.initializeFieldsWithRetry()
    this.cacheEstimateOptions()
    this.normalizeInitialProjectSelection()
    this.bindGlobalSearchTracking()
    this.bindProjectEstimateSync()
    this.bindFormSubmit()
    this.applyEstimateProjectFilter()
    this.refreshToggleButtons()
  }

  reinitializeAfterStream() {
    this.restoreFieldState()
    this.rebuildSelect2Instances()
    this.initializeAttempts = 0
    this.clearInitializeRetryTimer()
    this.clearReinitializeDelayTimer()
    this.initializeFieldsWithRetry()
    this.reinitializeDelayTimer = window.setTimeout(() => {
      this.initializeFieldsWithRetry()
      this.reinitializeDelayTimer = null
    }, 120)
    this.cacheEstimateOptions()
    this.applyEstimateProjectFilter()
    this.refreshToggleButtons()
  }

  disconnect() {
    this.clearPendingRestoreTimer()
    this.clearInitializeRetryTimer()
    this.clearReinitializeDelayTimer()
    this.clearOptionsRequestDebounceTimer()
    this.restoreFieldState()
    this.unbindFormSubmit()
    this.unbindGlobalSearchTracking()
    this.unbindProjectEstimateSync()
    this.destroyFields()
  }

  captureSearchBeforeToggle(event) {
    const fieldId = event.currentTarget.dataset.select22For
    const field = this.fieldTargets.find((target) => target.id === fieldId)
    if (!field) return

    this.captureSearchTerm(field)
  }

  toggleAll(event) {
    event.preventDefault()

    const fieldId = event.params.id
    const field = this.fieldTargets.find((target) => target.id === fieldId)
    if (!field) return

    this.captureSearchTerm(field)
    const options = this.toggleScopeOptions(field)
    if (options.length === 0) return

    const allSelected = options.every((option) => option.selected)

    options.forEach((option) => {
      option.selected = !allSelected
    })

    this.triggerFieldChange(field)
    this.refreshToggleButtons()
  }

  dateRangeChanged() {
    if (this.isBulkUpdating || this.isInitializingFields) return
    this.scheduleOptionsRefresh()
  }

  scheduleOptionsRefresh() {
    this.clearOptionsRequestDebounceTimer()
    this.optionsRequestDebounceTimer = window.setTimeout(async () => {
      try {
        await this.refreshOptionsForDateRange()
      } catch (_error) {
        // Keep the current options if the refresh call fails.
      } finally {
        this.optionsRequestDebounceTimer = null
      }
    }, 150)
  }

  clearOptionsRequestDebounceTimer() {
    if (!this.optionsRequestDebounceTimer) return
    window.clearTimeout(this.optionsRequestDebounceTimer)
    this.optionsRequestDebounceTimer = null
  }

  initializeFields() {
    const $ = this.jquery()
    if (!this.select2Ready($)) return false
    if (this.fieldTargets.length === 0) return false

    let initializedCount = 0
    this.isInitializingFields = true

    try {
      this.fieldTargets.forEach((field) => {
        const $field = $(field)
        if ($field.data("select2")) {
          initializedCount += 1
          return
        }

        $field.select2({
          width: "100%",
          closeOnSelect: false,
          placeholder: field.dataset.placeholder || "Select options",
          allowClear: false,
          dropdownAutoWidth: false
        })

        const $container = $field.next(".select2")
        $container.css({ width: "100%", maxWidth: "100%" })
        $container.on("input.select22 keyup.select22", ".select2-search__field", () => this.refreshToggleButtons())

        $field.on("change.select22", () => this.refreshToggleButtons())
        $field.on("select2:open.select22", () => {
          this.activeSearchFieldId = field.id
          this.refreshToggleButtons()
        })
        $field.on("select2:closing.select22", () => this.captureSearchTerm(field))
        $field.on("select2:close.select22", () => {
          if (this.activeSearchFieldId === field.id) this.activeSearchFieldId = null
          this.refreshToggleButtons()
        })

        initializedCount += 1
      })

      return initializedCount === this.fieldTargets.length
    } finally {
      this.isInitializingFields = false
    }
  }

  initializeFieldsWithRetry() {
    const initialized = this.initializeFields()
    if (initialized) {
      this.refreshToggleButtons()
      return
    }

    if (this.initializeAttempts >= this.maxInitializeAttempts) {
      console.warn("[select22] Select2 not ready after max retries")
      return
    }

    this.initializeAttempts += 1
    this.clearInitializeRetryTimer()
    this.initializeRetryTimer = window.setTimeout(() => this.initializeFieldsWithRetry(), 100)
  }

  clearInitializeRetryTimer() {
    if (!this.initializeRetryTimer) return
    window.clearTimeout(this.initializeRetryTimer)
    this.initializeRetryTimer = null
  }

  clearReinitializeDelayTimer() {
    if (!this.reinitializeDelayTimer) return
    window.clearTimeout(this.reinitializeDelayTimer)
    this.reinitializeDelayTimer = null
  }

  allFieldsInitialized() {
    const $ = this.jquery()
    if (!this.select2Ready($)) return false
    if (this.fieldTargets.length === 0) return false

    return this.fieldTargets.every((field) => !!$(field).data("select2"))
  }

  clearPendingRestoreTimer() {
    if (!this.pendingRestoreTimer) return
    window.clearTimeout(this.pendingRestoreTimer)
    this.pendingRestoreTimer = null
  }

  destroyFields() {
    const $ = this.jquery()
    if (!this.select2Ready($)) return

    this.fieldTargets.forEach((field) => {
      const $field = $(field)
      const $container = $field.next(".select2")
      $container.off("input.select22 keyup.select22", ".select2-search__field")
      $field.off(".select22")
      if ($field.data("select2")) {
        $field.select2("destroy")
      }
    })
  }

  rebuildSelect2Instances() {
    this.destroyFields()
    this.captureOriginalFieldNames()
  }

  refreshToggleButtons() {
    this.toggleTargets.forEach((button) => {
      const fieldId = button.dataset.select22For
      const field = this.fieldTargets.find((target) => target.id === fieldId)
      if (!field) return

      const options = this.toggleScopeOptions(field)
      const allSelected = options.length > 0 && options.every((option) => option.selected)
      const hasSearch = this.searchTermFor(field).length > 0

      if (hasSearch) {
        button.textContent = allSelected ? "Clear Filtered" : "Select Filtered"
      } else {
        button.textContent = allSelected ? "Clear All" : "Select All"
      }
    })
  }

  toggleScopeOptions(field) {
    const options = this.selectableOptions(field)
    const term = this.searchTermFor(field)
    if (!term) return options

    const visibleOptions = this.visibleResultOptions(field)
    if (visibleOptions.length > 0) return visibleOptions

    const normalizedTerm = this.normalizeText(term)
    return options.filter((option) => this.normalizeText(option.text).includes(normalizedTerm))
  }

  selectableOptions(field) {
    return Array.from(field.options).filter((option) => option.value !== "" && !option.disabled)
  }

  searchTermFor(field) {
    const $ = this.jquery()
    if (!this.select2Ready($)) return ""

    const term = this.liveSearchTermFor(field)
    if (term.length > 0) return term

    return (this.searchTerms[field.id] || "").toString().trim()
  }

  normalizeText(value) {
    return value.toString().toLowerCase().trim()
  }

  liveSearchTermFor(field) {
    const $ = this.jquery()
    if (!this.select2Ready($)) return ""

    const select2 = $(field).data("select2")
    const candidates = [
      select2?.dropdown?.$search?.val(),
      select2?.selection?.$search?.val(),
      $(field).next(".select2").find(".select2-search__field").first().val()
    ]

    for (const candidate of candidates) {
      const term = (candidate || "").toString().trim()
      if (term.length > 0) {
        this.setSearchTerm(field.id, term)
        return term
      }
    }

    return ""
  }

  visibleResultOptions(field) {
    const $ = this.jquery()
    if (!this.select2Ready($)) return []

    const select2 = $(field).data("select2")
    const $results = select2?.dropdown?.$results
    if (!$results || $results.length === 0) return []

    const optionsByValue = new Map(this.selectableOptions(field).map((option) => [option.value.toString(), option]))
    const visible = []

    $results.find(".select2-results__option--selectable").each((_, node) => {
      const data = $(node).data("data")
      const element = data?.element
      const candidateValue = (element?.value ?? data?.id ?? "").toString()
      const option = optionsByValue.get(candidateValue)
      if (option) visible.push(option)
    })

    return visible
  }

  captureSearchTerm(field) {
    const liveTerm = this.liveSearchTermFor(field)
    if (liveTerm.length > 0) {
      this.setSearchTerm(field.id, liveTerm)
      return
    }

    const prior = (this.searchTerms[field.id] || "").toString()
    this.setSearchTerm(field.id, prior)
  }

  bindGlobalSearchTracking() {
    const $ = this.jquery()
    if (!this.select2Ready($)) return

    $(document).off("input.select22-global keyup.select22-global", ".select2-container--open .select2-search__field")
    $(document).on("input.select22-global keyup.select22-global", ".select2-container--open .select2-search__field", (event) => {
      if (!this.activeSearchFieldId) return
      const value = (event.target.value || "").toString().trim()
      this.setSearchTerm(this.activeSearchFieldId, value)
      this.refreshToggleButtons()
    })
  }

  unbindGlobalSearchTracking() {
    const $ = this.jquery()
    if (!this.select2Ready($)) return

    $(document).off("input.select22-global keyup.select22-global", ".select2-container--open .select2-search__field")
    this.activeSearchFieldId = null
  }

  setSearchTerm(fieldId, value) {
    this.searchTerms[fieldId] = value.toString()
  }

  cacheEstimateOptions() {
    const field = this.estimateField()
    if (!field) return

    this.estimateOptionCache = Array.from(field.options).map((option, index) => ({
      value: option.value,
      text: option.text,
      projectId: (option.dataset.projectId || "").toString(),
      defaultSelected: option.selected,
      index
    }))
  }

  normalizeInitialProjectSelection() {
    const projectField = this.projectField()
    if (!projectField) return

    const hasExplicitSelection = Array.from(projectField.options).some((option) => option.defaultSelected)
    if (hasExplicitSelection) return

    Array.from(projectField.options).forEach((option) => {
      option.selected = false
    })

    this.triggerFieldChange(projectField)
  }

  bindProjectEstimateSync() {
    const $ = this.jquery()
    const projectField = this.projectField()
    if (!projectField) return

    this.projectEstimateSyncHandler = () => {
      if (this.isBulkUpdating) return
      this.applyEstimateProjectFilter()
    }
    projectField.addEventListener("change", this.projectEstimateSyncHandler)

    if ($ && typeof $.fn.select2 === "function") {
      $(projectField).on(
        "change.select22-project select2:select.select22-project select2:unselect.select22-project",
        this.projectEstimateSyncHandler
      )
    }
  }

  unbindProjectEstimateSync() {
    const $ = this.jquery()
    const projectField = this.projectField()
    if (!projectField || !this.projectEstimateSyncHandler) return

    projectField.removeEventListener("change", this.projectEstimateSyncHandler)
    if ($ && typeof $.fn.select2 === "function") {
      $(projectField).off(".select22-project")
    }
  }

  bindFormSubmit() {
    this.formElement = this.element.closest("form")
    if (!this.formElement) return

    this.formSubmitHandler = () => this.prepareCompactSubmitPayload()
    this.formElement.addEventListener("submit", this.formSubmitHandler, true)
  }

  unbindFormSubmit() {
    if (!this.formElement || !this.formSubmitHandler) return
    this.formElement.removeEventListener("submit", this.formSubmitHandler, true)
    this.formSubmitHandler = null
    this.formElement = null
  }

  prepareCompactSubmitPayload() {
    const form = this.formElement || this.element.closest("form")
    if (!form) return

    this.removeCompactInputs(form)

    const payload = {}
    this.compactPayloadFieldTargets().forEach((field) => {
      const scopeKey = SCOPE_KEY_BY_FIELD_ID[field.id]
      if (!scopeKey) return

      const selectedValues = Array.from(field.selectedOptions)
        .map((option) => option.value?.toString())
        .filter((value) => value && value.length > 0)
      payload[scopeKey] = selectedValues
    })

    const compactInput = document.createElement("input")
    compactInput.type = "hidden"
    compactInput.name = "ai_query[selected_ids_by_scope_compact]"
    compactInput.value = JSON.stringify(payload)
    compactInput.dataset.select22Compact = "true"
    form.appendChild(compactInput)

    // Avoid Rack multipart part limits by excluding the raw multi-select arrays
    // from submission. The compact JSON payload above is the single source of truth.
    this.stripFieldNamesForSubmit()
    this.clearPendingRestoreTimer()
    this.pendingRestoreTimer = window.setTimeout(() => this.restoreFieldState(), 0)
  }

  removeCompactInputs(form) {
    form.querySelectorAll('input[data-select22-compact="true"]').forEach((node) => node.remove())
  }

  restoreFieldState() {
    const form = this.formElement || this.element.closest("form")
    if (form) this.removeCompactInputs(form)

    this.fieldTargets.forEach((field) => {
      const originalName = field.dataset.select22OriginalName
      if (typeof originalName === "string") {
        if (originalName.length > 0) {
          field.setAttribute("name", originalName)
        } else {
          field.removeAttribute("name")
        }
      }
      field.disabled = false
    })
  }

  captureOriginalFieldNames() {
    this.fieldTargets.forEach((field) => {
      if (!Object.prototype.hasOwnProperty.call(field.dataset, "select22OriginalName")) {
        field.dataset.select22OriginalName = field.getAttribute("name") || ""
      }
    })
  }

  stripFieldNamesForSubmit() {
    this.compactPayloadFieldTargets().forEach((field) => {
      if (!Object.prototype.hasOwnProperty.call(field.dataset, "select22OriginalName")) {
        field.dataset.select22OriginalName = field.getAttribute("name") || ""
      }
      field.removeAttribute("name")
    })
  }

  compactPayloadFieldTargets() {
    return this.fieldTargets.filter((field) => Object.prototype.hasOwnProperty.call(SCOPE_KEY_BY_FIELD_ID, field.id))
  }

  applyEstimateProjectFilter() {
    const estimateField = this.estimateField()
    const optionCache = Array.isArray(this.estimateOptionCache) ? this.estimateOptionCache : []
    if (!estimateField || optionCache.length === 0) return

    const selectedProjectIds = this.selectedProjectIds()
    const shouldFilterByProjects = selectedProjectIds.size > 0
    const selectedEstimateIds = new Set(Array.from(estimateField.selectedOptions).map((option) => option.value))

    const scoped = optionCache
      .filter((entry) => !shouldFilterByProjects || (entry.projectId && selectedProjectIds.has(entry.projectId)))
      .sort((a, b) => a.index - b.index)

    estimateField.innerHTML = ""

    scoped.forEach((entry) => {
      const selected = selectedEstimateIds.has(entry.value) ||
        (selectedEstimateIds.size === 0 && entry.defaultSelected)

      const option = new Option(entry.text, entry.value, false, selected)
      if (entry.projectId) option.dataset.projectId = entry.projectId
      estimateField.add(option)
    })

    this.triggerFieldChange(estimateField)
  }

  async refreshOptionsForDateRange() {
    if (!this.hasOptionsUrlValue) return
    if (!this.hasStartDateTarget || !this.hasEndDateTarget) return

    const startDate = String(this.startDateTarget.value || "")
    const endDate = String(this.endDateTarget.value || "")
    if (!startDate || !endDate) return
    if (!this.isDateRequestValueValid(startDate, this.startDateTarget)) return
    if (!this.isDateRequestValueValid(endDate, this.endDateTarget)) return
    if (startDate > endDate) return

    const requestKey = this.currentOptionsRequestKey()
    if (requestKey.length === 0) return
    if (requestKey === this.lastAppliedOptionsRequestKey) return
    if (requestKey === this.pendingOptionsRequestKey) return

    this.pendingOptionsRequestKey = requestKey
    const requestToken = ++this.optionsRequestToken
    const query = new URLSearchParams({ start_date: startDate, end_date: endDate })
    this.selectedEstimateTypes().forEach((estimateType) => query.append("estimate_types[]", estimateType))
    try {
      const response = await fetch(`${this.optionsUrlValue}?${query.toString()}`, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) return
      const payload = await response.json()
      if (requestToken !== this.optionsRequestToken) return

      this.applyDateScopedOptions(payload)
      this.lastAppliedOptionsRequestKey = requestKey
    } finally {
      if (this.pendingOptionsRequestKey === requestKey) {
        this.pendingOptionsRequestKey = null
      }
    }
  }

  applyDateScopedOptions(payload) {
    const projectField = this.projectField()
    const estimateField = this.estimateField()
    const userField = this.userField()
    if (!projectField || !estimateField) return

    const selectedValues = {
      projects: new Set(Array.from(projectField.selectedOptions).map((option) => option.value.toString())),
      users: new Set(Array.from(userField?.selectedOptions || []).map((option) => option.value.toString())),
      estimates: new Set(Array.from(estimateField.selectedOptions).map((option) => option.value.toString()))
    }

    this.isBulkUpdating = true
    this.setFieldOptions(projectField, payload.projects, selectedValues.projects)
    if (userField) this.setFieldOptions(userField, payload.users, selectedValues.users)
    this.setFieldOptions(estimateField, payload.estimates, selectedValues.estimates, (option, item) => {
      if (item.project_id) option.dataset.projectId = item.project_id.toString()
    })

    this.triggerFieldChange(projectField)
    if (userField) this.triggerFieldChange(userField)
    this.triggerFieldChange(estimateField)
    this.cacheEstimateOptions()
    this.isBulkUpdating = false

    this.applyEstimateProjectFilter()
    this.refreshToggleButtons()
  }

  setFieldOptions(field, rawItems, selectedSet, decorateOption = null) {
    const items = Array.isArray(rawItems) ? rawItems : []
    const fragment = document.createDocumentFragment()

    items.forEach((item) => {
      const id = item?.id?.toString()
      const label = item?.label?.toString()
      if (!id || !label) return

      const option = new Option(label, id, false, selectedSet.has(id))
      if (decorateOption) decorateOption(option, item)
      fragment.appendChild(option)
    })

    field.innerHTML = ""
    field.appendChild(fragment)
  }

  isDateRequestValueValid(value, input) {
    const text = String(value || "")
    if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return false

    const year = Number.parseInt(text.slice(0, 4), 10)
    if (!Number.isFinite(year) || year < 1900 || year > 3000) return false

    const min = String(input?.min || "")
    if (/^\d{4}-\d{2}-\d{2}$/.test(min) && text < min) return false

    const max = String(input?.max || "")
    if (/^\d{4}-\d{2}-\d{2}$/.test(max) && text > max) return false

    return true
  }

  selectedProjectIds() {
    const field = this.projectField()
    if (!field) return new Set()

    return new Set(Array.from(field.selectedOptions).map((option) => option.value))
  }

  projectField() {
    return this.fieldTargets.find((target) => target.id === "ai_query_project_ids")
  }

  estimateField() {
    return this.fieldTargets.find((target) => target.id === "ai_query_estimate_ids")
  }

  userField() {
    return this.fieldTargets.find((target) => target.id === "ai_query_user_ids")
  }

  estimateTypeField() {
    return this.fieldTargets.find((target) => target.id === "ai_query_estimate_types")
  }

  selectedEstimateTypes() {
    const field = this.estimateTypeField()
    if (!field) return []

    return Array.from(field.selectedOptions)
      .map((option) => option.value?.toString())
      .filter((value) => value && value.length > 0)
  }

  currentOptionsRequestKey() {
    if (!this.hasStartDateTarget || !this.hasEndDateTarget) return ""

    const startDate = String(this.startDateTarget.value || "").trim()
    const endDate = String(this.endDateTarget.value || "").trim()
    if (!startDate || !endDate) return ""

    const estimateTypes = this.selectedEstimateTypes().slice().sort().join(",")
    return [startDate, endDate, estimateTypes].join("|")
  }

  triggerFieldChange(field) {
    const $ = this.jquery()
    if (this.select2Ready($)) {
      $(field).trigger("change")
      return
    }

    field.dispatchEvent(new Event("change", { bubbles: true }))
  }

  jquery() {
    return window.jQuery || window.$ || jquery
  }

  select2Ready($) {
    return !!$ && typeof $.fn?.select2 === "function"
  }
}
